import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ArrowUpRight, AlertTriangle } from "lucide-react";
import { useAuth } from "@/app/auth";
import { supabase } from "@/lib/supabase";
import { ago, dateShort, money } from "@/lib/format";
import { ACTIVITY_TYPE_LABEL } from "@/types";
import { QueryError } from "@/components/shared/QueryError";

type Money = Record<string, number>;
interface Overview {
  // Un compte sans rôle reçoit un objet vide : chaque champ est donc optionnel.
  money?: { invoiced_month: Money; invoiced_prev_month: Money; collected_month: Money; credited_month: Money; due: Money; overdue: Money };
  todo?: Record<string, number>;
  months?: { month: string; invoiced: Money; collected: Money }[];
  top_overdue?: { id: string; number: string; client: string; amount_due: number; currency: string; days: number }[];
}

function useDashboard() {
  return useQuery({
    queryKey: ["dashboard"],
    refetchInterval: 60_000,
    queryFn: async () => {
      const [overview, recent] = await Promise.all([
        supabase.rpc("dashboard_overview"),
        supabase.from("activities").select("id, type, subject, content, occurred_at, clients(id, legal_name)").neq("type", "system").order("occurred_at", { ascending: false }).limit(6),
      ]);
      if (overview.error) throw overview.error;
      if (recent.error) throw recent.error;
      return { o: overview.data as unknown as Overview, recent: recent.data ?? [] };
    },
  });
}

const sum = (m: Money | undefined) => Object.values(m ?? {}).reduce((a, b) => a + Number(b), 0);
const fmt = (m: Money | undefined) => {
  const e = Object.entries(m ?? {}).filter(([, v]) => Number(v) !== 0);
  return e.length ? e.map(([c, v]) => money(v, c as "EUR" | "USD")).join(" · ") : "—";
};
const monthLabel = (m: string) => new Date(`${m}-01T00:00:00`).toLocaleDateString("fr-FR", { month: "short", year: "2-digit" });

export default function DashboardPage() {
  const { user } = useAuth();
  const { data, isLoading, error } = useDashboard();
  const o = data?.o;
  const hi = (user?.user_metadata?.full_name as string | undefined)?.split(" ")[0];

  if (error) return <div><h1 className="mb-4 font-heading text-2xl font-bold">Tableau de bord</h1><QueryError error={error} /></div>;

  const todo = o?.todo ?? {};
  const months = o?.months ?? [];
  const topOverdue = o?.top_overdue ?? [];
  // Sans rôle, la base ne renvoie rien : on le dit au lieu d'afficher des zéros.
  const noAccess = Boolean(o) && !o?.money;
  const actions: { key: string; label: string; to: string; urgent?: boolean }[] = [
    { key: "invoices_overdue", label: "facture(s) échue(s)", to: "/factures", urgent: true },
    { key: "disputes_open", label: "contestation(s) ouverte(s)", to: "/leads", urgent: true },
    { key: "leads_review", label: "lead(s) à revoir", to: "/leads", urgent: true },
    { key: "bank_unmatched", label: "transaction(s) à rapprocher", to: "/banque" },
    { key: "invoices_to_send", label: "facture(s) à émettre ou envoyer", to: "/factures" },
    { key: "orders_pending", label: "commande(s) en attente de validation", to: "/commandes" },
    { key: "leads_to_deliver", label: "lead(s) à livrer", to: "/commandes" },
    { key: "contracts_to_sign", label: "contrat(s) en attente de signature", to: "/contrats" },
    { key: "contracts_renew", label: "contrat(s) à renouveler", to: "/contrats" },
    { key: "quotes_open", label: "devis en cours", to: "/devis" },
    { key: "quotes_expiring", label: "devis qui expirent sous 7 jours", to: "/devis" },
    { key: "tasks_today", label: "tâche(s) du jour", to: "/clients" },
  ].filter((a) => Number(todo[a.key] ?? 0) > 0);

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-bold">Tableau de bord</h1>
        <p className="text-sm text-muted-foreground">Bonjour{hi ? ` ${hi}` : ""} — l'argent du mois, et ce qui attend une action.</p>
      </div>

      {noAccess && <p className="glass mb-6 rounded-2xl p-5 text-sm text-muted-foreground">Ton compte n'a encore aucun rôle : personne ne t'a donné accès aux données. Demande à un administrateur de t'attribuer un rôle dans Paramètres → Utilisateurs.</p>}

      <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Card label="Facturé ce mois" value={fmt(o?.money?.invoiced_month)} hint={`mois précédent : ${fmt(o?.money?.invoiced_prev_month)}`} to="/factures" loading={isLoading} testId="db-invoiced" />
        <Card label="Encaissé ce mois" value={fmt(o?.money?.collected_month)} hint={sum(o?.money?.credited_month) > 0 ? `avoirs émis : ${fmt(o?.money?.credited_month)}` : "paiements reçus (carte, virement)"} to="/banque" loading={isLoading} testId="db-collected" />
        <Card label="Restant dû" value={fmt(o?.money?.due)} hint="factures émises non soldées" to="/factures" loading={isLoading} testId="db-due" />
        <Card label="Échu" value={fmt(o?.money?.overdue)} hint={Number(todo.invoices_overdue ?? 0) > 0 ? `${todo.invoices_overdue} facture(s) en retard` : "rien en retard"} to="/factures" tone={sum(o?.money?.overdue) > 0 ? "alert" : undefined} loading={isLoading} testId="db-overdue" />
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_360px]">
        <section className="glass min-w-0 rounded-2xl p-5" data-testid="db-todo">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">À faire</h2>
          {isLoading ? <p className="text-sm text-muted-foreground">Chargement…</p> : actions.length === 0 ? (
            <p className="text-sm text-muted-foreground">Rien en attente : aucun retard, aucune revue, aucun rapprochement.</p>
          ) : (
            <ul className="divide-y divide-border/60">
              {actions.map((a) => (
                <li key={a.key}>
                  <Link to={a.to} className="group flex items-center justify-between gap-3 py-2.5 text-sm">
                    <span className="flex items-center gap-2">
                      {a.urgent && <AlertTriangle className="h-4 w-4 text-destructive" />}
                      <span className={`font-heading text-lg font-bold ${a.urgent ? "text-destructive" : ""}`}>{Number(todo[a.key])}</span>
                      <span>{a.label}</span>
                    </span>
                    <ArrowUpRight className="h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                  </Link>
                </li>
              ))}
            </ul>
          )}

          {topOverdue.length > 0 && (
            <>
              <h3 className="mb-2 mt-5 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Les plus en retard</h3>
              <ul className="space-y-1 text-sm">
                {topOverdue.map((i) => (
                  <li key={i.id} className="flex items-center justify-between gap-3">
                    <Link to={`/factures/${i.id}`} className="hover:underline"><span className="font-mono text-xs">{i.number}</span> · {i.client}</Link>
                    <span className="shrink-0 tabular-nums text-destructive">{money(i.amount_due, i.currency as "EUR" | "USD")} · +{i.days} j</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>

        <div className="min-w-0 space-y-4">
          <section className="glass min-w-0 rounded-2xl p-5">
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">12 derniers mois</h2>
            {isLoading ? <p className="text-sm text-muted-foreground">Chargement…</p> : (
              <div className="overflow-x-auto"><table className="w-full text-sm">
                <thead><tr className="text-xs uppercase tracking-wider text-muted-foreground"><th className="text-left font-medium">Mois</th><th className="text-right font-medium">Facturé</th><th className="text-right font-medium">Encaissé</th></tr></thead>
                <tbody>
                  {months.map((m) => {
                    const inv = sum(m.invoiced), col = sum(m.collected);
                    return (
                      <tr key={m.month} className={inv === 0 && col === 0 ? "text-muted-foreground/60" : ""}>
                        <td className="whitespace-nowrap py-1 pr-3">{monthLabel(m.month)}</td>
                        <td className="whitespace-nowrap py-1 pl-3 text-right tabular-nums">{inv === 0 ? "—" : fmt(m.invoiced)}</td>
                        <td className="whitespace-nowrap py-1 pl-3 text-right tabular-nums">{col === 0 ? "—" : fmt(m.collected)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table></div>
            )}
          </section>

          <section className="glass min-w-0 rounded-2xl p-5">
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Activité récente</h2>
            {!data?.recent.length ? <p className="text-sm text-muted-foreground">Rien pour l'instant.</p> : (
              <ul className="space-y-2 text-sm">
                {data.recent.map((a) => {
                  const client = a.clients as { id: string; legal_name: string } | null;
                  return (
                    <li key={a.id}>
                      <div className="flex justify-between gap-2">
                        <span className="font-medium">{a.subject ?? ACTIVITY_TYPE_LABEL[a.type as keyof typeof ACTIVITY_TYPE_LABEL] ?? a.type}</span>
                        <span className="shrink-0 text-xs text-muted-foreground">{ago(a.occurred_at)}</span>
                      </div>
                      {client && <Link to={`/clients/${client.id}`} className="text-xs text-muted-foreground hover:underline">{client.legal_name}</Link>}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>
      </div>
      <p className="mt-6 text-xs text-muted-foreground">Chiffres au {dateShort(new Date())}, actualisés chaque minute. Les montants ne sont jamais convertis : chaque devise est affichée telle quelle.</p>
    </div>
  );
}

function Card({ label, value, hint, to, tone, loading, testId }: { label: string; value: string; hint?: string; to: string; tone?: "alert"; loading?: boolean; testId?: string }) {
  return (
    <Link to={to} className="glass group rounded-2xl p-5 transition-transform hover:-translate-y-0.5" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div className="text-sm text-muted-foreground">{label}</div>
        <ArrowUpRight className="h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
      </div>
      <div className={`mt-2 font-heading text-2xl font-extrabold tracking-tight ${tone === "alert" ? "text-destructive" : ""}`}>{loading ? "…" : value}</div>
      {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
    </Link>
  );
}
